"use client";

import { createContext, useContext } from "react";

import type {
  CustomerWindowAuthorization,
  CustomerWindowTab,
} from "@/lib/realtime/customer-projection/window-contract";

import {
  HUB_POLL_INTERVALS_MS,
  HUB_SAFETY_NET_POLL_MS,
} from "@/dashboard/features/hub/lib/constants";

export const HUB_FOLDER_COUNTS_SAFETY_NET_POLL_MS = 5 * 60_000;
export const HUB_CUSTOMER_LIST_SAFETY_NET_POLL_MS = 5 * 60_000;

export type HubRealtimeContextValue = {
  /** Hub events control the base polling safety net. */
  connected: boolean;
  /** Customer-list invalidations use the dedicated list capability and its five-minute safety net. */
  customerListsConnected: boolean;
  folderCountsConnected: boolean;
};

export const HubRealtimeContext = createContext<HubRealtimeContextValue>({
  connected: false,
  customerListsConnected: false,
  folderCountsConnected: false,
});

export type CustomerListWindowTransport = {
  authorization: CustomerWindowAuthorization;
  tenantIds: readonly string[];
  subscribe: (
    subscriptionId: string,
    tab: CustomerWindowTab,
    onMessage: (message: unknown) => void
  ) => () => void;
};

export const CustomerListWindowTransportContext = createContext<CustomerListWindowTransport | null>(
  null
);

export function hubPollInterval(connected: boolean, pollingIntervalMs: number): number {
  return connected ? HUB_SAFETY_NET_POLL_MS : pollingIntervalMs;
}

export function useHubPollInterval(pollingIntervalMs: number): number {
  const { connected } = useContext(HubRealtimeContext);
  return hubPollInterval(connected, pollingIntervalMs);
}

export function customerListPollInterval(
  { connected, customerListsConnected }: HubRealtimeContextValue,
  pollingIntervalMs: number
): number {
  if (customerListsConnected) {
    return HUB_CUSTOMER_LIST_SAFETY_NET_POLL_MS;
  }
  return hubPollInterval(connected, pollingIntervalMs);
}

export function useCustomerListPollInterval(pollingIntervalMs: number): number {
  return customerListPollInterval(useContext(HubRealtimeContext), pollingIntervalMs);
}

export function folderCountsPollInterval({
  connected,
  folderCountsConnected,
}: HubRealtimeContextValue): number {
  return folderCountsConnected
    ? HUB_FOLDER_COUNTS_SAFETY_NET_POLL_MS
    : hubPollInterval(connected, HUB_POLL_INTERVALS_MS.folderCounts);
}

/** Events prompt a refresh; the safety interval repairs missed events and replica lag. */
export function useFolderCountsPollInterval(): number {
  return folderCountsPollInterval(useContext(HubRealtimeContext));
}
